import React, { useState, useEffect, useRef, useCallback } from 'react';

const BACKEND = () => (window.ENV?.REACT_APP_BACKEND_URL || window.location.origin);

// Project-level FULL-SHEET header images (a floor plan + example/reference
// photos) that print full-width at the top of every trade/export sheet.
const SheetHeaderImages = ({ projectId }) => {
  const [images, setImages] = useState([]);
  const [busy, setBusy] = useState(false);
  const [msg, setMsg] = useState(null); // { tone:'ok'|'error', text }
  const planRef = useRef(null);
  const exRef = useRef(null);

  const load = useCallback(async () => {
    try {
      const r = await fetch(`${BACKEND()}/api/projects/${projectId}/sheet-images`);
      const d = await r.json();
      setImages(d.images || []);
    } catch { /* ignore */ }
  }, [projectId]);

  useEffect(() => { load(); }, [load]);

  const upload = async (files, kind) => {
    if (!files || !files.length) return;
    setBusy(true); setMsg(null);
    try {
      const fd = new FormData();
      Array.from(files).forEach(f => fd.append('files', f));
      fd.append('kind', kind);
      const r = await fetch(`${BACKEND()}/api/projects/${projectId}/sheet-images`, { method: 'POST', body: fd });
      const d = await r.json();
      if (r.ok) { setMsg({ tone: 'ok', text: `Added ${d.added} ${kind === 'floor-plan' ? 'floor plan' : 'example'} image(s)` }); await load(); }
      else setMsg({ tone: 'error', text: d.detail || 'Upload failed' });
    } catch (e) { setMsg({ tone: 'error', text: 'Upload failed' }); }
    setBusy(false);
    if (planRef.current) planRef.current.value = '';
    if (exRef.current) exRef.current.value = '';
  };

  const remove = async (id) => {
    if (!window.confirm('Remove this image from the sheet headers?')) return;
    try {
      const r = await fetch(`${BACKEND()}/api/projects/${projectId}/sheet-images/${id}`, { method: 'DELETE' });
      if (r.ok) await load();
    } catch { /* ignore */ }
  };

  const plans = images.filter(i => i.kind === 'floor-plan');
  const examples = images.filter(i => i.kind !== 'floor-plan');

  const Tile = ({ img }) => (
    <div className="relative group rounded-lg overflow-hidden border border-[#D4A574]/40 bg-black/40" data-testid={`sheet-image-${img.id}`}>
      <img src={`${BACKEND()}${img.url}`} alt={img.name} className="w-full h-28 object-cover" />
      <button
        data-testid={`sheet-image-delete-${img.id}`}
        onClick={() => remove(img.id)}
        className="absolute top-1 right-1 bg-red-600 hover:bg-red-500 text-white text-xs w-6 h-6 rounded-full flex items-center justify-center opacity-0 group-hover:opacity-100 transition-opacity"
        title="Remove"
      >✕</button>
      {img.caption ? <div className="px-2 py-1 text-[10px] text-[#D4C5A9] truncate">{img.caption}</div> : null}
    </div>
  );

  return (
    <div
      data-testid="sheet-header-images"
      className="rounded-2xl p-6 border border-[#D4A574]/60 mb-8"
      style={{ background: 'linear-gradient(135deg, rgba(0,0,0,0.95) 0%, rgba(30,30,30,0.9) 50%, rgba(0,0,0,0.95) 100%)' }}
    >
      <div className="flex items-start justify-between gap-4 flex-wrap mb-4">
        <div className="flex items-start gap-4">
          <div className="text-5xl">🗺️</div>
          <div>
            <h3 className="text-2xl font-bold text-[#D4A574]">Sheet Header Images</h3>
            <p className="text-sm text-[#B49B7E] max-w-xl">
              Upload a <strong>floor plan</strong> and <strong>example photos</strong>. They print full-width
              at the top of every trade sheet, so each trade sees the plan and the look of the home.
            </p>
          </div>
        </div>
        <div className="flex gap-3 flex-wrap">
          <input ref={planRef} type="file" accept="image/*" multiple className="hidden"
            data-testid="sheet-image-plan-input"
            onChange={(e) => upload(e.target.files, 'floor-plan')} />
          <button data-testid="upload-floor-plan-btn" disabled={busy}
            onClick={() => planRef.current && planRef.current.click()}
            className="px-4 py-2 bg-[#D4A574] hover:bg-[#E2B989] text-black rounded-lg text-sm font-bold disabled:opacity-60">
            ＋ Floor Plan
          </button>
          <input ref={exRef} type="file" accept="image/*" multiple className="hidden"
            data-testid="sheet-image-example-input"
            onChange={(e) => upload(e.target.files, 'example')} />
          <button data-testid="upload-example-btn" disabled={busy}
            onClick={() => exRef.current && exRef.current.click()}
            className="px-4 py-2 border border-[#D4A574] text-[#D4A574] hover:bg-[#D4A574]/10 rounded-lg text-sm font-bold disabled:opacity-60">
            ＋ Example Photos
          </button>
        </div>
      </div>

      {busy && <div className="text-sm text-[#D4A574] mb-3">Uploading…</div>}
      {msg && <div className={`text-sm mb-3 ${msg.tone === 'ok' ? 'text-emerald-400' : 'text-red-400'}`} data-testid="sheet-image-msg">{msg.text}</div>}

      {plans.length > 0 && (
        <div className="mb-4">
          <div className="text-xs font-bold tracking-widest text-[#B49B7E] mb-2">FLOOR PLAN</div>
          <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 gap-3">
            {plans.map(img => <Tile key={img.id} img={img} />)}
          </div>
        </div>
      )}
      {examples.length > 0 && (
        <div>
          <div className="text-xs font-bold tracking-widest text-[#B49B7E] mb-2">EXAMPLE / REFERENCE</div>
          <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 gap-3">
            {examples.map(img => <Tile key={img.id} img={img} />)}
          </div>
        </div>
      )}
      {images.length === 0 && !busy && (
        <div className="text-sm text-[#8a7960] italic">No header images yet — add a floor plan and example photos above.</div>
      )}
    </div>
  );
};

export default SheetHeaderImages;
